import type { Lead, LeadInteractionType } from '@/types';

import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Textarea, Select, SelectItem } from '@heroui/react';

interface AddInteractionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  isSubmitting: boolean;
  onSubmit: (type: LeadInteractionType, note: string, outcome?: string | null) => void;
}

const INTERACTION_TYPES: Array<{ key: LeadInteractionType; label: string }> = [
  { key: 'call', label: 'Call' },
  { key: 'email', label: 'Email' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'whatsapp', label: 'WhatsApp' }
];

export function AddInteractionModal({ isOpen, onOpenChange, lead, isSubmitting, onSubmit }: AddInteractionModalProps) {
  const [type, setType] = React.useState<LeadInteractionType>('call');
  const [note, setNote] = React.useState('');
  const [outcome, setOutcome] = React.useState('');

  React.useEffect(() => {
    if (!isOpen) {
      setType('call');
      setNote('');
      setOutcome('');
    }
  }, [isOpen]);

  const handleSubmit = React.useCallback(() => {
    if (!note.trim()) {
      return;
    }

    onSubmit(type, note.trim(), outcome.trim() || null);
    setType('call');
    setNote('');
    setOutcome('');
  }, [type, note, outcome, onSubmit]);

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  if (!lead) return null;

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} size="md">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Add Interaction for {lead.domain}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                <Select
                  label="Type"
                  selectedKeys={new Set([type])}
                  onSelectionChange={(keys) => {
                    const selectedType = Array.from(keys)[0] as LeadInteractionType;
                    if (selectedType) {
                      setType(selectedType);
                    }
                  }}
                  isRequired
                >
                  {INTERACTION_TYPES.map((item) => (
                    <SelectItem key={item.key}>{item.label}</SelectItem>
                  ))}
                </Select>

                <Textarea
                  label="Note"
                  placeholder="Enter interaction details..."
                  value={note}
                  onValueChange={setNote}
                  minRows={4}
                  isRequired
                />

                <Textarea
                  label="Outcome (Optional)"
                  placeholder="Enter outcome if any..."
                  value={outcome}
                  onValueChange={setOutcome}
                  minRows={2}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={onClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting} isDisabled={!note.trim()}>
                Add Interaction
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

